import PropTypes from "prop-types";
import { useState, useEffect, useRef, memo } from "react";
import { cn } from "@shared/lib/utils";

// Optimized image component with proper srcset, sizes, and lazy loading
const OptimizedImage = memo(function OptimizedImage({
  src,
  alt,
  className = "",
  loading = "lazy",
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  width,
  height,
  srcSet,
  onImageLoad = () => {},
  onImageError = () => {},
  priority = false,
}) {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef(null);
  const [isInView, setIsInView] = useState(priority);

  // Generate srcset if not provided
  const imageSrcSet =
    srcSet ||
    (src.includes("http")
      ? `${src}?w=400 400w, ${src}?w=800 800w, ${src}?w=1200 1200w`
      : undefined);

  // Use Intersection Observer for truly lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "50px" },
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoading(false);
    onImageLoad();
  };

  const handleError = () => {
    setError(true);
    setIsLoading(false);
    onImageError();
  };

  // Placeholder dimensions to prevent layout shift
  const aspectRatio = width && height ? height / width : 1;
  const placeholderStyle = {
    paddingBottom: width && height ? `${aspectRatio * 100}%` : undefined,
  };

  return (
    <div
      ref={imgRef}
      className={cn("relative overflow-hidden", className)}
      style={!width && !height ? placeholderStyle : undefined}
    >
      {/* Low quality placeholder for immediate display */}
      {isLoading && (
        <div
          className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse"
          role="presentation"
          aria-hidden="true"
        />
      )}

      {/* Main image - only load when in view */}
      {(isInView || priority) && !error && (
        <img
          src={src}
          alt={alt}
          srcSet={imageSrcSet}
          sizes={sizes}
          width={width}
          height={height}
          loading={priority ? "eager" : loading}
          decoding={priority ? "sync" : "async"}
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            "w-full h-full object-cover",
            isLoading && "opacity-0",
            !isLoading && "opacity-100 transition-opacity duration-300",
          )}
        />
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <div className="text-center p-4">
            <svg
              className="h-8 w-8 text-gray-400 mx-auto mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-sm text-gray-500">Image unavailable</p>
          </div>
        </div>
      )}
    </div>
  );
});

OptimizedImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  loading: PropTypes.oneOf(["lazy", "eager"]),
  sizes: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  srcSet: PropTypes.string,
  onImageLoad: PropTypes.func,
  onImageError: PropTypes.func,
  priority: PropTypes.bool,
};

export default OptimizedImage;
